// Reusable HTML5-canvas signature pad. Works on mouse, trackpad, and touch.
// Emits a PNG File via onSaved(file).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Eraser, Check, Loader2 } from 'lucide-react';

const SignaturePad = ({ onSaved, onCleared, saving, savedAt, height = 180, testIdPrefix = 'sig' }) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPtRef = useRef(null);
  const dirtyRef = useRef(false);
  const [isEmpty, setIsEmpty] = useState(true);

  // Fit canvas to CSS width with device-pixel scaling
  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.floor(rect.width * dpr);
    canvas.height = Math.floor(rect.height * dpr);
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0f172a';
    // White background so the resulting PNG isn't fully transparent — helps compliance readability
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
  }, []);

  useEffect(() => {
    setupCanvas();
    const onResize = () => setupCanvas();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [setupCanvas]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastPtRef.current = getPos(e);
  };
  const moveDraw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const cur = getPos(e);
    const last = lastPtRef.current;
    if (last) {
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(cur.x, cur.y);
      ctx.stroke();
    }
    lastPtRef.current = cur;
    dirtyRef.current = true;
    if (isEmpty) setIsEmpty(false);
  };
  const endDraw = () => {
    drawingRef.current = false;
    lastPtRef.current = null;
    if (dirtyRef.current) capture();  // auto-capture the moment a stroke finishes — no Save button needed
  };

  const clear = () => {
    setupCanvas();
    dirtyRef.current = false;
    setIsEmpty(true);
    onCleared?.();
  };

  const capture = () => {
    const canvas = canvasRef.current;
    if (!canvas || !dirtyRef.current) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      onSaved(new File([blob], 'signature.png', { type: 'image/png' }));
    }, 'image/png');
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`${testIdPrefix}-pad`}>
      <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Sign below</span>
        {(savedAt || !isEmpty) && (
          <span className="text-[11px] text-emerald-600 font-semibold inline-flex items-center gap-1">
            <Check className="w-3 h-3" /> Captured
          </span>
        )}
      </div>
      <canvas
        ref={canvasRef}
        style={{ height, width: '100%', touchAction: 'none', cursor: 'crosshair', display: 'block' }}
        onMouseDown={startDraw}
        onMouseMove={moveDraw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={moveDraw}
        onTouchEnd={endDraw}
        data-testid={`${testIdPrefix}-canvas`}
      />
      <div className="px-3 py-2 bg-slate-50 border-t border-slate-200 flex items-center gap-2">
        <button
          type="button"
          onClick={clear}
          data-testid={`${testIdPrefix}-clear`}
          className="inline-flex shrink-0 whitespace-nowrap items-center gap-1.5 rounded-full border border-slate-300 hover:bg-slate-100 text-slate-700 text-xs font-semibold px-3 py-1.5"
        >
          <Eraser className="w-3 h-3" /> Clear
        </button>
        <span className="text-[10px] text-slate-400 ml-auto inline-flex items-center gap-1">{saving && <Loader2 className="w-3 h-3 animate-spin" />}Sign with your mouse, trackpad or finger — it saves automatically.</span>
      </div>
    </div>
  );
};

export default SignaturePad;
